import React from "react";
import { Link } from "react-router-dom";
import { Crown, Flame, Heart, Hourglass, Moon, Orbit, Sparkles, Sun, Zap } from "lucide-react";
import { Stagger, StaggerItem } from "../lib/motion";
import { GRAHA_DETAIL, MUKHI_DETAIL, MUKHI_SPECIALS, RASHI_DETAIL } from "../content/nav";

const GRAHA_ICON = {
  sun: Sun, moon: Moon, mangal: Flame, budh: Zap, guru: Crown,
  shukra: Heart, shani: Hourglass, rahu: Orbit, ketu: Sparkles,
};

export function GrahaQuickNav() {
  return (
    <Stagger
      className="grid grid-cols-3 gap-2 sm:grid-cols-[repeat(auto-fill,minmax(125px,1fr))] sm:gap-3"
      stagger={0.045}
      data-testid="graha-quicknav"
    >
      {GRAHA_DETAIL.map((g) => {
        const Icon = GRAHA_ICON[g.slug];
        return (
          <StaggerItem key={g.slug}>
            <Link
              to={`/graha/${g.slug}`}
              data-testid={`graha-cell-${g.slug}`}
              className="card-s group flex h-full flex-col items-center justify-center border-rule text-center transition-colors duration-300 hover:border-gold hover:bg-surface"
              style={{ padding: "20px 8px" }}
            >
              <Icon className="h-[18px] w-[18px] text-gold transition-transform duration-500 group-hover:-translate-y-0.5" aria-hidden="true" />
              <span className="deva mt-3 block text-sm leading-none text-deep">{g.deva}</span>
              <span className="ui mt-2 block text-[11px] font-medium leading-none text-ink transition-colors group-hover:text-gold">
                {g.name}
              </span>
              <span className="ui mt-1.5 block text-[10px] leading-tight text-dim">{g.stone}</span>
            </Link>
          </StaggerItem>
        );
      })}
    </Stagger>
  );
}

export function MukhiQuickNav({ includeSpecials = true }) {
  const cells = includeSpecials ? [...MUKHI_DETAIL, ...MUKHI_SPECIALS] : MUKHI_DETAIL;
  return (
    <Stagger
      className="snap-row no-scrollbar -mx-5 gap-2.5 px-5 pb-1 sm:mx-0 sm:grid sm:grid-cols-[repeat(auto-fill,minmax(140px,1fr))] sm:px-0 sm:pb-0"
      stagger={0.03}
      data-testid="mukhi-quicknav"
    >
      {cells.map((m) => (
        <StaggerItem key={m.to} className="max-sm:w-[190px]">
          <Link
            to={m.to}
            data-testid={`mukhi-cell-${m.mukhi || m.slug}`}
            className="group flex h-full items-center gap-3 border border-rule px-3 py-2.5 transition-colors duration-300 hover:border-gold hover:bg-surface"
          >
            <span className="relative h-11 w-11 shrink-0 overflow-hidden border border-rule" aria-hidden="true">
              <img
                src={`/img/products/${m.mukhi ? `${m.mukhi}-mukhi` : m.slug}-400.webp`}
                alt=""
                loading="lazy"
                width="44"
                height="44"
                className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
            </span>
            <span className="min-w-0">
              <span className="deva block truncate text-xs leading-none text-deep">{m.deva}</span>
              <span className="ui mt-1 block truncate text-[11px] font-medium leading-none text-ink transition-colors group-hover:text-gold">
                {m.deity}
              </span>
              <span className="ui mt-1 block truncate text-[10px] leading-none text-dim">{m.graha}</span>
            </span>
          </Link>
        </StaggerItem>
      ))}
    </Stagger>
  );
}

export function RashiQuickNav() {
  return (
    <Stagger
      className="snap-row no-scrollbar -mx-5 gap-2.5 px-5 pb-1 sm:mx-0 sm:grid sm:grid-cols-[repeat(auto-fill,minmax(140px,1fr))] sm:px-0 sm:pb-0"
      stagger={0.035}
      data-testid="rashi-quicknav"
    >
      {RASHI_DETAIL.map((r) => (
        <StaggerItem key={r.slug} className="max-sm:w-[190px]">
          <Link
            to={`/shop/by-rashi/${r.slug}`}
            data-testid={`rashi-cell-${r.slug}`}
            className="group flex h-full items-center gap-3 border border-rule px-3.5 py-3 transition-colors duration-300 hover:border-gold hover:bg-surface"
          >
            <span
              className="text-lg leading-none text-gold"
              style={{ fontFamily: "'Source Serif 4', Georgia, serif", fontVariantEmoji: "text" }}
              aria-hidden="true"
            >
              {r.symbol}
            </span>
            <span className="min-w-0">
              <span className="deva block text-xs leading-none text-deep">{r.deva}</span>
              <span className="ui mt-1 block truncate text-[11px] font-medium leading-none text-ink transition-colors group-hover:text-gold">
                {r.english}
              </span>
              <span className="ui mt-1 block truncate text-[10px] leading-none text-dim">{r.stone}</span>
            </span>
          </Link>
        </StaggerItem>
      ))}
    </Stagger>
  );
}
